// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Standalone read view for a single SOP Factory artifact. Fetches the artifact
// by id (GET /api/v1/sop-factory/artifacts/{id}) and renders its metadata and
// procedure steps. Linked from the SOP list (Equipment Docs) and the alarm
// detail's response-procedure SOP id.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import { getArtifact } from '../../services/sopFactoryService';
import { Loading } from '../../components/shell/ScreenHelpers';
import type { Artifact, ArtifactStatus } from '../../types';
import './SopArtifactDetail.css';

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; artifact: Artifact | null };

// Status → Helios theme color token. Rendered as a themed pill (a custom span,
// like ScreenHelpers' SevTag/DxTag) so it tracks the g100/g10 theme rather than
// relying on Carbon's Tag surface, which reads as light in hs-page content.
const STATUS_COLOR: Record<string, string> = {
  approved: 'var(--green)',
  in_review: 'var(--blue)',
  draft: 'var(--helios)',
  deprecated: 'var(--text-helper)',
};

function StatusPill({ status }: { status: ArtifactStatus | undefined }) {
  if (!status) return null;
  const color = STATUS_COLOR[status] ?? 'var(--text-helper)';
  return (
    <span className="sop-tag" style={{ color, borderColor: color }}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

export default function SopArtifactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setState({ status: 'loading' });
    getArtifact(id)
      .then(artifact => {
        if (!cancelled) setState({ status: 'ok', artifact });
      })
      .catch(err => {
        if (!cancelled) {
          setState({ status: 'error', message: (err as Error).message ?? 'Request failed' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state.status === 'loading') return <Loading />;

  if (state.status === 'error' || !state.artifact) {
    return (
      <div className="hs-page">
        <p>{state.status === 'error' ? state.message : 'SOP not found.'}</p>
        <Button kind="ghost" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );
  }

  const artifact = state.artifact;
  const categories = artifact.categories ?? {};
  const steps = artifact.payload?.steps ?? [];
  const chips = [
    categories.domain,
    categories.equipment_type,
    categories.procedure_type,
    categories.risk_tier ? `risk: ${categories.risk_tier}` : undefined,
  ].filter((c): c is string => Boolean(c));

  return (
    <div className="hs-page sop-detail">
      <button type="button" className="sop-back" onClick={() => navigate(-1)}>
        <ArrowLeft size={16} /> Back
      </button>

      <div className="sop-head">
        <div className="hs-card-row" style={{ marginBottom: 8 }}>
          <StatusPill status={artifact.status} />
          <span className="sop-id">{artifact.artifact_id}</span>
          {artifact.version && <span className="sop-version">v{artifact.version}</span>}
        </div>
        <h1 className="hs-title">{artifact.title ?? artifact.artifact_id}</h1>
        {artifact.payload?.description_of_work && (
          <p className="sop-desc">{artifact.payload.description_of_work}</p>
        )}
        {chips.length > 0 && (
          <div className="hs-card-row" style={{ marginTop: 8, flexWrap: 'wrap' }}>
            {chips.map(c => (
              <span key={c} className="sop-chip">
                {c}
              </span>
            ))}
          </div>
        )}
      </div>

      <section className="sop-card">
        <div className="sop-card-title">Procedure</div>
        {steps.length === 0 ? (
          <p className="sop-empty">No procedure steps published for this SOP.</p>
        ) : (
          <ol className="sop-steps">
            {steps.map((s, i) => (
              <li key={s.seq ?? i}>
                {s.verb && <span className="sop-step-verb">{s.verb}</span>}
                <span className="sop-step-instruction">{s.instruction}</span>
                {s.is_gate && <span className="sop-flag sop-flag-gate">Gate</span>}
                {s.hold_point && <span className="sop-flag sop-flag-hold">Hold point</span>}
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}
